import "./scrollToDatePanel.scss";

import PropTypes from "prop-types";
import PubSub from "pubsub-js";
import React from "react";
import { connect } from "react-redux";

import CalendarPicker from "../../components/calendarPicker";
import FormButton from "../../components/form/FormButton";
import InspectorField from "../../components/inspector/InspectorField";
import InspectorRow from "../../components/inspector/InspectorRow";
import { withScreenContext } from "../../components/screen/ScreenContext";
import { DATE_FIELD_START } from "../../constants/dateFields";
import PubSubTopics from "../../constants/pubSubTopics";
import screenModes from "../../constants/screenModes";
import { appStateOperations } from "../../modules/appState";
import { calendarSelectors } from "../../modules/definition/calendar";
import dateFunctions, { isDateValid } from "../../modules/definition/calendar/dateFunctions";
import { timelineRangeSelectors } from "../../modules/timelineRange";
import { PropTypeShapes } from "../../types";
import { DateRecord } from "../../types/immutableRecords/model/data/DateRecord";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import preprocessorQuery from "../../utils/preprocessorQuery";

class ScrollToDatePanel extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      date: DateRecord(),
      endDate: DateRecord()
    };

    this.setDate = this.setDate.bind(this);
    this.setEndDate = this.setEndDate.bind(this);
    this.onOk = this.onOk.bind(this);
    this.onCalendarPickerPrecisionChange = this.onCalendarPickerPrecisionChange.bind(this);
  }

  setDate(date) {
    let newDate = date;
    let endDate = this.state.endDate;

    // Check the date is within the timeline's range
    if (isDateValid(this.props.dateRangeFrom) && newDate.timestamp < this.props.dateRangeFrom.timestamp) {
      newDate = this.props.dateRangeFrom;
    } else if (isDateValid(this.props.dateRangeTo) && newDate.timestamp > this.props.dateRangeTo.timestamp) {
      newDate = this.props.dateRangeTo;
    }

    // Clear end date if it's now before the start date
    if (isDateValid(endDate) && newDate.timestamp >= endDate.timestamp) {
      endDate = null;
    }
    this.setState({ date: newDate, endDate: endDate });

    const { onDateChange } = this.props;
    if (onDateChange) {
      onDateChange(newDate);
    }
  }

  setEndDate(date) {
    let newDate = date;

    // End date is only allowed if there's a start date
    if (!isDateValid(this.state.date)) {
      return;
    }

    // Reject change if end date is before the start date
    if (isDateValid(newDate) && this.state.date.timestamp >= newDate.timestamp) {
      return;
    }

    // Check the date is within the timeline's range
    if (isDateValid(this.props.dateRangeTo) && newDate.timestamp > this.props.dateRangeTo.timestamp) {
      newDate = this.props.dateRangeTo;
    }

    this.setState({ endDate: newDate });
  }

  onOk() {
    let endDate = null;
    if (isDateValid(this.state.endDate) && this.state.date.timestamp !== this.state.endDate.timestamp) {
      endDate = this.state.endDate;
      PubSub.publish(PubSubTopics.scrollToDualDate, { startDate: this.state.date, endDate: endDate });
    } else {
      PubSub.publish(PubSubTopics.scrollToDate, this.state.date, endDate);
    }
    this.props.toggleScrollToDatePanel();
  }

  onCalendarPickerPrecisionChange(newPrecision) {
    const newDate = this.state.date.set("precision", newPrecision);
    this.setDate(newDate);
  }

  render() {
    const isMobile = preprocessorQuery.isMobile();

    const panelHeader = isMobile ? null : (
      <div className="scrollToDatePanelHeader">
        <span>Scroll to Date</span>
      </div>
    );

    const confirmButton = isMobile ? null : (
      <InspectorRow>
        <InspectorField>
          <FormButton
            primary={true}
            disabled={!isDateValid(this.state.date)}
            label="Scroll to Date"
            onClick={this.onOk}
          />
        </InspectorField>
      </InspectorRow>
    );

    const fieldLabel = isMobile ? null : "Choose Date";

    let isDual = false;
    //#!if harvard === true
    if (this.props.screenContext && this.props.screenContext.screenMode === screenModes.Desktop) {
      isDual = true;
    }
    //#!endif

    return (
      <div className={`scrollToDatePanel`}>
        {panelHeader}
        <InspectorRow>
          <InspectorField label={fieldLabel}>
            <div className="calendarPickerChildren">
              <CalendarPicker
                calendar={this.props.calendar}
                startDate={this.state.date}
                endDate={this.state.endDate}
                allowUncertainDates={false}
                isDual={isDual}
                onStartDateChange={this.setDate}
                onEndDateChange={this.setEndDate}
                dateForNow={dateFunctions.dateForNow(this.props.calendar)}
                selectedDateField={DATE_FIELD_START}
                onPrecisionChange={this.onCalendarPickerPrecisionChange}
              />
            </div>
          </InspectorField>
        </InspectorRow>
        {confirmButton}
      </div>
    );
  }
}

ScrollToDatePanel.propTypes = {
  calendar: PropTypeShapes.calendar.isRequired
};

const mapStateToProps = state => {
  return {
    calendar: calendarSelectors.getCalendar(state.present),
    dateRangeFrom: timelineRangeSelectors.getRangeFrom(state.present),
    dateRangeTo: timelineRangeSelectors.getRangeTo(state.present)
  };
};

const readonlyActions = {
  toggleScrollToDatePanel: appStateOperations.toggleScrollToDatePanel
};

const editingActions = {};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

ScrollToDatePanel.propTypes = {
  calendar: PropTypeShapes.calendar.isRequired,
  toggleScrollToDatePanel: PropTypes.func.isRequired,
  onDateChange: PropTypes.func,
  dateRangeFrom: PropTypeShapes.date,
  dateRangeTo: PropTypeShapes.date,
  //#!if harvard === true
  // From withScreenContext
  screenContext: PropTypes.object
  //#!endif
};

let exportedView = ScrollToDatePanel;
//#!if harvard === true
exportedView = withScreenContext(ScrollToDatePanel);
//#!endif
export default connect(mapStateToProps, mapDispatchToProps)(exportedView);
